import { Link } from "react-router-dom";
import { useState } from "react";
import { AffiliationLinks } from "@/components/dpe/AffiliationLinks";
import { AmortizationChart } from "@/components/dpe/AmortizationChart";
import { EnergyLabel } from "@/components/dpe/EnergyLabel";
import { useCatalogs } from "@/hooks/useDpe";
import { useLogement } from "@/hooks/useLogements";
import { useResolvedAffiliations } from "@/hooks/useAffiliations";
import {
  useApplicableImprovements,
  useBouquetsPreview,
  useCreateScenario,
  useDeleteScenario,
  useRecalculateScenario,
  useScenarios,
} from "@/hooks/useScenarios";
import type {
  BouquetPreview,
  ImprovementApplicability,
  ImprovementBreakdown,
  RenovationObjective,
  Scenario,
} from "@/api/scenarios";
import { OBJECTIVE_LABELS } from "@/api/scenarios";
import type { BouquetOption, ImprovementOption, RecommendedBouquet } from "@/api/catalogs";
import { ApiError } from "@/api/client";
import {
  buildSuggestedSet,
  rankByObjective,
} from "@/utils/objectiveRanking";

/**
 * Panneau scénarios de travaux.
 *
 * Affiché sous le résultat DPE. Permet de :
 *   - choisir un bouquet prédéfini en 1 clic
 *   - composer un scénario sur mesure
 *   - voir le résultat (étiquette après travaux, coût, aides, ROI)
 *   - supprimer un scénario
 */
export function ScenariosPanel({
  logementId,
  currentLabel,
}: {
  logementId: string;
  currentLabel: string;
}) {
  const { data: scenarios } = useScenarios(logementId);
  const { data: catalogs } = useCatalogs();
  const { data: logement } = useLogement(logementId);
  const { data: applicability, isLoading: applicabilityLoading, error: applicabilityError } =
    useApplicableImprovements(logementId);
  const { data: bouquetsPreview } = useBouquetsPreview(logementId);
  // Module financement : revenu net mensuel du foyer (optionnel) pour estimer
  // la capacité d'emprunt. Pré-rempli depuis le RFR fiscal s'il est connu.
  const [monthlyIncome, setMonthlyIncome] = useState<string>("");
  const effectiveMonthlyIncome =
    Number(monthlyIncome) > 0
      ? Number(monthlyIncome)
      : logement?.tax_reference_income_eur
        ? Math.round(logement.tax_reference_income_eur / 12)
        : 0;
  // Helper : préfixe utilisé côté backend pour les bouquets recommandés
  // par classe DPE (vs bouquets génériques sans préfixe).
  const previewFor = (key: string, recommended = false): BouquetPreview | undefined =>
    bouquetsPreview?.items[recommended ? `recommended:${key}` : key];

  // Debug visible : si la query échoue, on log dans la console pour
  // diagnostiquer rapidement quand l'utilisateur reporte "je vois toujours
  // les bouquets non pertinents". À retirer en production.
  if (applicabilityError) {
    // eslint-disable-next-line no-console
    console.error("[applicable-improvements] query failed:", applicabilityError);
  }
  const create = useCreateScenario(logementId);
  const remove = useDeleteScenario(logementId);
  const recalc = useRecalculateScenario(logementId);
  const [serverError, setServerError] = useState<string | null>(null);
  // V5 — Objectif de rénovation (sélecteur de classement, non persisté).
  // Défaut : ROI financier. Si le logement est en F/G, on pré-sélectionne
  // l'objectif "location" pour aider l'utilisateur à sortir du statut
  // passoire énergétique.
  const isPassoire = ["F", "G"].includes(currentLabel);
  const [objective, setObjective] = useState<RenovationObjective>(
    isPassoire ? "rental" : "roi",
  );
  // Le RFR et la taille du foyer sont stockés au niveau LOGEMENT (V3) —
  // chaque logement peut être occupé par un foyer fiscal différent.
  const fiscalProvided =
    logement?.household_size != null &&
    logement?.tax_reference_income_eur != null;

  // Verdict global de rentabilité : si l'utilisateur a déjà testé au moins 2
  // scénarios et qu'AUCUN n'est rentable (ROI > 20 ans pour tous), on cesse
  // de proposer de nouveaux bouquets — c'est un signal clair que pour ce
  // logement, aucun travail standard ne se rentabilise sur 20 ans.
  const allTestedScenariosNonRentable =
    !!scenarios &&
    scenarios.length >= 2 &&
    scenarios.every(
      (s) => s.relevance === "non_rentable" || s.relevance === "inadapte",
    );

  // Handler central d'erreur pour les mutations create — utilisé partout
  // où on appelle `create.mutate({ name, improvements })`.
  const onCreateError = (err: unknown) => {
    if (err instanceof ApiError) {
      if (err.code === "INAPPLICABLE_IMPROVEMENT") {
        setServerError(
          "Une amélioration ne s'applique pas à votre logement (ex : pas de toiture déclarée). Complétez la saisie puis réessayez.",
        );
      } else if (err.code === "SCENARIO_LIMIT_REACHED") {
        setServerError(
          "Limite de 5 scénarios atteinte. Supprimez-en un avant d'en créer un nouveau.",
        );
      } else if (err.code === "SCENARIO_DUPLICATE") {
        setServerError(
          "Ce scénario existe déjà. Modifiez le nom ou la liste d'améliorations, ou supprimez l'existant avant d'en créer un nouveau.",
        );
      } else {
        setServerError(err.message);
      }
    }
  };

  return (
    <section className="rounded border border-gray-200 bg-white p-6">
      <h2 className="text-xl font-bold">Simuler des travaux</h2>
      <p className="mt-1 text-sm text-gray-600">
        Choisissez un bouquet prédéfini, ou composez votre propre scénario.
        Vos étiquettes seront recalculées avec les améliorations appliquées.
      </p>

      <div className="mt-6 rounded bg-amber-50 p-3 text-sm text-amber-900">
        <strong>⚠️ Estimations indicatives.</strong> Les coûts et les aides
        peuvent varier significativement selon votre région, l&apos;état du
        logement et votre situation fiscale. Pour passer à l&apos;action
        (trouver un artisan RGE, vérifier votre éligibilité aux aides),
        rendez-vous sur l&apos;onglet <strong>« Passer à l&apos;action »</strong>
        {" "}ci-dessus.
      </div>

      {!fiscalProvided && (
        <div className="mt-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <strong>Estimez vos aides au plus juste.</strong> Renseignez le{" "}
          <Link
            to={`/logements/${logementId}`}
            className="underline font-medium"
          >
            revenu fiscal de référence et la taille du foyer de ce logement
          </Link>{" "}
          pour calculer le tier MaPrimeRénov' (bleu / jaune / violet / rose).
          Sans cela, on applique le tier <em>violet</em> (intermédiaires) par
          défaut.
        </div>
      )}

      {/* V5 — Sélecteur d'objectif de rénovation. Ré-ordonne la liste
        * des améliorations applicables dans la section "Conseillé pour
        * votre objectif" ci-dessous. Non persisté : changer d'objectif
        * ne modifie aucun scénario sauvegardé. */}
      <div className="mt-6">
        <label
          htmlFor="renovation-objective"
          className="text-sm font-semibold text-gray-800"
        >
          🎯 Quel est votre objectif de rénovation ?
        </label>
        <p className="mt-1 text-xs text-gray-600">
          On classe les améliorations par impact sur votre objectif, puis on
          vous propose un set à simuler en 1 clic.
        </p>
        <select
          id="renovation-objective"
          value={objective}
          onChange={(e) => setObjective(e.target.value as RenovationObjective)}
          className="mt-2 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          {(Object.keys(OBJECTIVE_LABELS) as RenovationObjective[]).map((k) => (
            <option key={k} value={k}>
              {OBJECTIVE_LABELS[k]}
            </option>
          ))}
        </select>
      </div>

      {/* V5 — Section "Conseillé pour votre objectif" : top améliorations
        * triées par l'objectif choisi + set suggéré top-4 (conflits
        * dédupliqués) + bouton "Simuler ce set" (1-clic). */}
      {applicability && catalogs?.improvements && (
        <ObjectiveRecommendations
          items={applicability.items}
          improvements={catalogs.improvements}
          exclusiveApplicators={catalogs.exclusive_applicators}
          baseLabel={applicability.base_label}
          objective={objective}
          onCreate={({ name, improvements: keys }) =>
            create.mutate(
              { name, improvements: keys },
              {
                onSuccess: () => setServerError(null),
                onError: onCreateError,
              },
            )
          }
          isCreating={create.isPending}
        />
      )}

      {/* Panneau "Recommandations confort d'été" — calculé pour CE logement
        * à partir de l'endpoint /applicable-improvements (qui inclut le
        * summer_score_delta de chaque amélioration). Visible AVANT toute
        * simulation pour aider à prioriser les travaux selon l'inconfort
        * estival, indépendamment de la rentabilité énergétique. */}
      {applicability && catalogs?.improvements && (
        <SummerComfortRecommendations
          baseScore={applicability.base_summer_score ?? 0}
          baseClasse={applicability.base_summer_classe ?? ""}
          items={applicability.items}
          improvements={catalogs.improvements}
        />
      )}

      {allTestedScenariosNonRentable ? (
        /* Verdict global : tous les bouquets testés sont non rentables sur
         * 20 ans. On masque les recommandations pour ne pas pousser à des
         * travaux qui ne se rentabiliseront pas, et on affiche un message
         * clair. Le builder sur-mesure reste accessible pour les utilisateurs
         * qui veulent tout de même explorer d'autres combinaisons. */
        <div className="mt-6 rounded border-2 border-orange-300 bg-orange-50 p-4">
          <h3 className="text-base font-bold text-orange-900">
            🔍 Aucun travail standard recommandable pour ce logement
          </h3>
          <p className="mt-2 text-sm text-orange-900">
            <strong>
              Tous les travaux envisageables ont une rentabilité supérieure à
              20 ans pour ce logement. Aucune recommandation.
            </strong>
          </p>
          <p className="mt-2 text-sm text-orange-900">
            Les {scenarios?.length ?? 0} scénarios testés ci-dessous ont tous
            un ROI {">"} 20 ans, ce qui signifie que les économies d'énergie
            attendues ne couvrent pas le coût des travaux dans cet horizon.
          </p>
          <p className="mt-2 text-sm text-orange-900">
            Cela peut s'expliquer par : un logement déjà bien isolé / un
            système de chauffage déjà performant / un usage modéré (peu
            d'économies absolues à dégager). Si vous souhaitez tout de même
            simuler une combinaison particulière (ex : remplacement d'un
            équipement en fin de vie, panneaux photovoltaïques en
            autoconsommation), vous pouvez composer un scénario sur mesure
            ci-dessous.
          </p>
        </div>
      ) : (
        <>
          {/* V4 — Bouquets structurés selon la classe DPE (guide DPE Annexe 2).
            * On NE LES AFFICHE PAS tant que l'applicabilité n'est pas chargée :
            * sinon l'utilisateur pourrait cliquer sur "Simuler" avec des
            * améliorations non pertinentes (PAC air/eau sur PAC géothermique).
            * Pendant le chargement (~1-2 sec), on montre un placeholder. */}
          {catalogs?.bouquets_by_class && currentLabel && catalogs.bouquets_by_class[currentLabel.toUpperCase()] && Object.keys(catalogs.bouquets_by_class[currentLabel.toUpperCase()]).length > 0 && (
            applicabilityLoading || !applicability ? (
              <div className="mt-6 rounded-lg border-2 border-dashed border-emerald-200 bg-emerald-50/50 p-4 text-sm text-emerald-800">
                {applicabilityError ? (
                  <>
                    ⚠️ Impossible de personnaliser les bouquets pour votre
                    logement (erreur réseau / serveur). Réessayez en
                    rafraîchissant la page.
                  </>
                ) : (
                  <>
                    ⏳ Analyse de la pertinence des bouquets pour votre
                    logement en cours… (quelques secondes)
                  </>
                )}
              </div>
            ) : (
              <RecommendedBouquetsSection
                currentLabel={currentLabel}
                bouquets={catalogs.bouquets_by_class[currentLabel.toUpperCase()]}
                improvements={catalogs.improvements ?? []}
                applicability={applicability.items}
                previewFor={(key) => previewFor(key, true)}
                onApply={(name, keys) => {
                  setServerError(null);
                  create.mutate({ name, improvements: keys }, { onError: onCreateError });
                }}
                disabled={create.isPending}
              />
            )
          )}

          {catalogs?.bouquets && applicability && (() => {
            // On filtre chaque bouquet en se basant sur l'applicabilité de
            // ses améliorations pour CE logement précis. Si aucune amélioration
            // du bouquet n'apporte de gain → bouquet masqué entièrement.
            const filteredBouquets = catalogs.bouquets
              .map((b) => {
                const allKeys = b.improvements ?? [];
                const applicableKeys = allKeys.filter(
                  (k) => applicability.items[k]?.applicable !== false,
                );
                const droppedCount = allKeys.length - applicableKeys.length;
                return { b, applicableKeys, droppedCount };
              })
              .filter(({ applicableKeys }) => applicableKeys.length > 0);

            if (filteredBouquets.length === 0) {
              return (
                <div className="mt-6 rounded bg-gray-50 p-3 text-xs text-gray-600">
                  <strong>Aucun bouquet générique n'apporterait de gain mesurable
                    à votre logement.</strong>{" "}
                  Vos équipements sont déjà au moins aussi performants que les
                  améliorations standards. Utilisez le builder sur-mesure
                  ci-dessous pour tester d'autres options (panneaux PV,
                  remplacement d'équipement en fin de vie, etc.).
                </div>
              );
            }

            return (
              <div className="mt-6">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                  Bouquets prédéfinis (génériques)
                </h3>
                {isLogementAlreadyEfficient(currentLabel) && (
                  <div className="mt-2 rounded bg-blue-50 p-2 text-xs text-blue-900">
                    <strong>Votre logement est déjà classé {currentLabel}.</strong>{" "}
                    Sur un bâtiment déjà performant, la plupart des gros bouquets
                    ont un ROI long (souvent &gt; 50 ans). Les gains d'énergie
                    restent réels mais l'investissement ne se rentabilise pas par
                    les économies de facture. Privilégiez :
                    <ul className="mt-1 list-disc pl-5">
                      <li>Le remplacement d'un équipement <strong>en fin de vie</strong> (chaudière &gt; 15 ans, ballon &gt; 12 ans)</li>
                      <li>Les <strong>panneaux photovoltaïques</strong> (autoconsommation, ROI 8-12 ans)</li>
                      <li>Un <strong>scénario sur mesure</strong> ciblé sur le poste le plus déperditif</li>
                    </ul>
                    Lancez une simulation pour voir la rentabilité réelle de chaque bouquet sur votre logement.
                  </div>
                )}
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  {filteredBouquets.map(({ b, applicableKeys, droppedCount }) => {
                    const preview = previewFor(b.key);
                    // "Recommandé" = la simulation préalable montre que
                    // c'est rentable (excellent/bon). On ne se base PLUS
                    // sur la seule classe DPE comme avant V4.
                    const isRentable =
                      preview?.relevance === "excellent" ||
                      preview?.relevance === "bon";
                    return (
                      <BouquetCard
                        key={b.key}
                        bouquet={b}
                        onClick={() => {
                          if (create.isPending) return;
                          setServerError(null);
                          create.mutate(
                            {
                              name: b.label ?? b.key,
                              improvements: applicableKeys,
                            },
                            { onError: onCreateError },
                          );
                        }}
                        disabled={create.isPending}
                        recommended={isRentable}
                        discouraged={
                          preview?.relevance === "non_rentable" ||
                          preview?.relevance === "inadapte"
                        }
                        applicableCount={applicableKeys.length}
                        droppedCount={droppedCount}
                        preview={preview}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {catalogs?.improvements && (
        <CustomScenarioBuilder
          improvements={catalogs.improvements}
          windPotential={applicability?.wind_potential ?? null}
          departmentCode={applicability?.department_code ?? null}
          onSubmit={(name, keys) => {
            setServerError(null);
            create.mutate({ name, improvements: keys }, { onError: onCreateError });
          }}
          loading={create.isPending}
        />
      )}

      {serverError && (
        <p role="alert" className="mt-4 text-sm text-red-700">
          {serverError}
        </p>
      )}

      {scenarios && scenarios.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Scénarios enregistrés ({scenarios.length})
          </h3>
          {scenarios.length >= 2 &&
            scenarios.every(
              (s) => s.relevance === "non_rentable" || s.relevance === "inadapte",
            ) && (
              <div className="mt-3 rounded border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900">
                <strong>💰 Aucun scénario n'est rentable sur 20 ans.</strong>{" "}
                Pour ce logement, les économies d'énergie attendues ne couvrent
                pas le coût des travaux dans un horizon de 20 ans. Ces travaux
                restent envisageables pour le confort, la valeur de revente, ou
                un remplacement d'équipement en fin de vie — mais ne se
                rentabiliseront pas par les seules économies d'énergie.
              </div>
            )}
          {/* Module financement : revenu mensuel (capacité d'emprunt). */}
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
            <label htmlFor="fin-income" className="text-gray-700">
              💳 Revenu net mensuel du foyer (optionnel) :
            </label>
            <input
              id="fin-income"
              type="number"
              min={0}
              step={100}
              value={monthlyIncome}
              onChange={(e) => setMonthlyIncome(e.target.value)}
              placeholder={
                logement?.tax_reference_income_eur
                  ? String(Math.round(logement.tax_reference_income_eur / 12))
                  : "ex. 2 500"
              }
              className="w-28 rounded border border-gray-300 px-2 py-1"
            />
            <span className="text-xs text-gray-500">
              € / mois — pour estimer si la mensualité de financement rentre dans
              votre budget.
            </span>
          </div>
          <ul className="mt-3 space-y-3">
            {scenarios.map((s) => (
              <ScenarioCard
                key={s.id}
                scenario={s}
                currentLabel={currentLabel}
                improvementsCatalog={catalogs?.improvements ?? []}
                monthlyIncome={effectiveMonthlyIncome}
                onDelete={() => remove.mutate(s.id)}
                onRecalculate={() => recalc.mutate(s.id)}
                recalculating={recalc.isPending}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Heuristiques de recommandation selon le label DPE actuel.
 *
 * - Label F/G (passoires) : tous les bouquets sont recommandés (gain
 *   énergétique garanti, MaPrimeRénov' avantageuse).
 * - Label D/E : tous les bouquets sont recommandés.
 * - Label A/B/C : aucun bouquet n'est marqué "Recommandé" — le gain et la
 *   rentabilité dépendent fortement du système et de l'isolation déjà en
 *   place. On laisse l'utilisateur lancer une simulation et on lui
 *   affichera ensuite la pertinence réelle (ok / marginal / non_rentable
 *   / inadapte) basée sur le calcul effectif. Les bouquets enveloppe
 *   sont en plus marqués "Peu pertinent ici" pour signaler le risque
 *   d'investissement à faible ROI sur un logement déjà bien isolé.
 */
const EFFICIENT_LABELS = new Set(["A", "B", "C"]);
// Note V4 : `isBouquetRecommended` et `isBouquetDiscouraged` ont été
// supprimés. Le badge "Recommandé / ROI > 20 ans" est désormais piloté
// par la simulation préalable (`useBouquetsPreview`) qui retourne la
// `relevance` réelle de chaque bouquet sur le logement précis.

function isLogementAlreadyEfficient(label: string | null | undefined): boolean {
  return !!label && EFFICIENT_LABELS.has(label.toUpperCase());
}

/**
 * V4 — Section "Bouquets recommandés selon votre classe DPE actuelle"
 * (guide officiel DPE Annexe 2). Affichée en priorité pour F/G et D/E.
 */
/**
 * Recommandations "confort d'été" — pour chaque amélioration du catalog,
 * calcule l'impact sur le score officiel de confort estival (Guide DPE
 * v3 p. 29-30) SUR CE LOGEMENT, et trie par delta décroissant.
 *
 * Source : endpoint /applicable-improvements (déjà appelé pour le
 * filtrage des bouquets) qui retourne `summer_score_delta` par geste.
 * Pas de calcul moteur supplémentaire — on réutilise la même requête.
 *
 * Affiché AVANT toute simulation pour aider l'utilisateur à choisir les
 * travaux qui amélioreront le plus le confort estival du logement,
 * indépendamment de la rentabilité financière.
 */
function SummerComfortRecommendations({
  baseScore,
  baseClasse,
  items,
  improvements,
}: {
  baseScore: number;
  baseClasse: string;
  items: Record<string, ImprovementApplicability>;
  improvements: ImprovementOption[];
}) {
  const labelFor = (key: string) =>
    improvements.find((i) => i.key === key)?.label ?? key;

  // Garde uniquement les améliorations qui apportent un gain CONFORT D'ÉTÉ
  // strictement positif (≥ 1 pt). Trié par delta décroissant.
  const ranked = Object.entries(items)
    .filter(
      ([, v]) =>
        v.summer_score_delta != null && (v.summer_score_delta ?? 0) >= 1,
    )
    .map(([key, v]) => ({
      key,
      delta: v.summer_score_delta ?? 0,
      classeBefore: v.summer_classe_before ?? baseClasse,
      classeAfter: v.summer_classe_after ?? baseClasse,
      scoreAfter: v.summer_score_after ?? baseScore,
    }))
    .sort((a, b) => b.delta - a.delta);

  // Si le logement est déjà confort d'été "bon" ET aucun travail ne fait
  // grimper le score, on n'affiche rien (pas de bruit visuel).
  if (ranked.length === 0) {
    if (baseClasse === "bon") return null;
    return (
      <div className="mt-4 rounded border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
        <strong>☀️ Confort d'été : {baseClasse || "non évalué"}</strong>
        {baseScore > 0 && (
          <span className="ml-1 text-amber-800">(score {baseScore}/100)</span>
        )}
        .{" "}
        Aucun bouquet standard n'améliorerait significativement le confort
        d'été. Pour l'améliorer concrètement : volets / BSO extérieurs sur
        les baies Sud/Est/Ouest, brasseurs d'air plafond, et ventilation
        nocturne — souvent absents du catalog standard de travaux.
      </div>
    );
  }

  const classeBadge = (c: string) => {
    if (c === "bon") return "bg-emerald-100 text-emerald-800";
    if (c === "moyen") return "bg-amber-100 text-amber-800";
    if (c === "insuffisant") return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-700";
  };

  return (
    <div className="mt-4 rounded border-2 border-amber-300 bg-amber-50/70 p-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-amber-900">
          ☀️ Améliorer le confort d'été — recommandations par impact
        </h3>
        <span className="text-xs text-amber-900">
          Confort d'été actuel :{" "}
          <span
            className={`rounded px-1.5 py-0.5 capitalize ${classeBadge(baseClasse)}`}
          >
            {baseClasse || "non évalué"}
          </span>{" "}
          (score <strong>{baseScore}</strong>/100)
        </span>
      </div>
      <p className="mb-3 text-[11px] text-amber-900">
        Indicateur officiel Guide DPE v3 (toiture, protections solaires
        extérieures, inertie, traversance). Les améliorations ci-dessous
        sont classées par <strong>gain de points de confort d'été</strong>{" "}
        sur votre logement précis. Cliquez sur "Simuler ce bouquet" plus
        bas pour intégrer une amélioration à un scénario complet.
      </p>
      <table className="w-full text-xs">
        <thead className="text-left text-amber-900/80">
          <tr>
            <th className="py-1 pr-2 font-medium">Amélioration</th>
            <th className="py-1 pr-2 font-medium">Classe (avant → après)</th>
            <th className="py-1 pr-2 font-medium text-right">Score</th>
            <th className="py-1 font-medium text-right">Gain</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((it) => (
            <tr key={it.key} className="border-t border-amber-200/70">
              <td className="py-1.5 pr-2 text-gray-800">{labelFor(it.key)}</td>
              <td className="py-1.5 pr-2 whitespace-nowrap">
                {it.classeBefore === it.classeAfter ? (
                  <span
                    className={`rounded px-1.5 py-0.5 capitalize ${classeBadge(it.classeBefore)}`}
                  >
                    {it.classeBefore || "—"}
                  </span>
                ) : (
                  <>
                    <span
                      className={`rounded px-1.5 py-0.5 capitalize ${classeBadge(it.classeBefore)}`}
                    >
                      {it.classeBefore || "—"}
                    </span>
                    <span className="mx-1 text-gray-400">→</span>
                    <span
                      className={`rounded px-1.5 py-0.5 capitalize ${classeBadge(it.classeAfter)}`}
                    >
                      {it.classeAfter || "—"}
                    </span>
                  </>
                )}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                → {it.scoreAfter}/100
              </td>
              <td className="py-1.5 text-right tabular-nums font-semibold text-emerald-700">
                +{it.delta} pts
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// V5 — Section "Conseillé pour votre objectif"
// =============================================================================

const SUGGESTED_SET_SIZE = 4;

function ObjectiveRecommendations({
  items,
  improvements,
  exclusiveApplicators,
  baseLabel,
  objective,
  onCreate,
  isCreating,
}: {
  items: Record<string, ImprovementApplicability>;
  improvements: ImprovementOption[];
  exclusiveApplicators: string[] | undefined;
  baseLabel: string | undefined;
  objective: RenovationObjective;
  onCreate: (args: { name: string; improvements: string[] }) => void;
  isCreating: boolean;
}) {
  // Classement complet (top 6 affichés pour transparence, top-4 pour le set).
  const ranked = rankByObjective(items, objective, baseLabel).slice(0, 6);
  const rankedAll = rankByObjective(items, objective, baseLabel);
  const { selected: suggestedKeys, excluded: excludedByConflict } =
    buildSuggestedSet(
      rankedAll,
      improvements,
      exclusiveApplicators,
      SUGGESTED_SET_SIZE,
    );

  if (ranked.length === 0) {
    return (
      <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        Aucune amélioration disponible pour cet objectif sur ce logement.
      </div>
    );
  }

  const suggestedMeta = suggestedKeys
    .map((k) => {
      const meta = improvements.find((i) => i.key === k);
      const item = items[k];
      return meta && item ? { key: k, label: meta.label ?? k, item } : null;
    })
    .filter((x): x is { key: string; label: string; item: ImprovementApplicability } => x !== null);

  const handleApplySuggested = () => {
    onCreate({
      name: `Optimisé : ${OBJECTIVE_LABELS[objective]}`,
      improvements: suggestedKeys,
    });
  };

  return (
    <div
      data-testid="objective-recommendations"
      className="mt-4 rounded border-2 border-blue-300 bg-blue-50/50 p-4"
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-bold text-blue-900">
          🎯 Conseillé pour votre objectif
        </h3>
        <span className="text-xs text-blue-700">
          Classement établi sur votre logement courant
        </span>
      </div>

      <ol className="space-y-1.5 text-sm text-gray-800">
        {ranked.map((key, idx) => {
          const meta = improvements.find((i) => i.key === key);
          const item = items[key];
          if (!meta || !item) return null;
          return (
            <li key={key} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-200 text-[10px] font-bold text-blue-800">
                  {idx + 1}
                </span>
                <span>{meta.label ?? key}</span>
              </span>
              <span className="text-xs tabular-nums text-gray-600">
                {describeMetric(objective, item)}
              </span>
            </li>
          );
        })}
      </ol>

      {suggestedMeta.length > 0 && (
        <div className="mt-4 rounded border border-blue-200 bg-white p-3">
          <div className="text-xs font-semibold text-gray-700">
            Set suggéré ({SUGGESTED_SET_SIZE} gestes, conflits exclusifs
            dédupliqués)
          </div>
          <ul className="mt-1 list-disc pl-5 text-xs text-gray-700">
            {suggestedMeta.map((s) => (
              <li key={s.key}>{s.label}</li>
            ))}
          </ul>
          {Object.keys(excludedByConflict).length > 0 && (
            <div className="mt-2 text-[11px] text-gray-500">
              Gestes écartés pour conflit d&apos;applicateur :
              {" "}
              {Object.entries(excludedByConflict)
                .map(
                  ([k, reason]) =>
                    `${improvements.find((i) => i.key === k)?.label ?? k} (${reason})`,
                )
                .join(" ; ")}
            </div>
          )}
          <button
            type="button"
            onClick={handleApplySuggested}
            disabled={isCreating || suggestedKeys.length === 0}
            data-testid="apply-objective-suggested-set"
            className="mt-3 rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {isCreating
              ? "Simulation…"
              : "Simuler ce set (1-clic)"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Formate la métrique clé de chaque objectif pour l'affichage dans la liste. */
function describeMetric(
  objective: RenovationObjective,
  item: ImprovementApplicability,
): string {
  switch (objective) {
    case "roi":
      if (item.roi_years == null) return "—";
      return `${item.roi_years.toFixed(1)} ans`;
    case "carbon":
      if (item.ges_per_m2_delta == null) return "—";
      return `−${item.ges_per_m2_delta.toFixed(1)} kgCO₂/m²·an`;
    case "winter_comfort":
      if (item.heating_kwh_ef_delta == null) return "—";
      return `−${Math.round(item.heating_kwh_ef_delta).toLocaleString("fr-FR")} kWh chauff.`;
    case "summer_comfort":
      if (item.summer_score_delta == null) return "—";
      return `+${item.summer_score_delta} pts`;
    case "rental":
      return item.label_after ? `→ ${item.label_after}` : "—";
  }
}

function RecommendedBouquetsSection({
  currentLabel,
  bouquets,
  improvements,
  applicability,
  previewFor,
  onApply,
  disabled,
}: {
  currentLabel: string;
  bouquets: Record<string, RecommendedBouquet>;
  improvements: ImprovementOption[];
  applicability: Record<string, ImprovementApplicability>;
  previewFor: (key: string) => BouquetPreview | undefined;
  onApply: (name: string, keys: string[]) => void;
  disabled: boolean;
}) {
  const labelFor = (key: string) =>
    improvements.find((i) => i.key === key)?.label ?? key;
  const isPasoire = ["F", "G"].includes(currentLabel.toUpperCase());

  // Filtrage par logement : pour chaque bouquet, on calcule combien
  // d'améliorations sont réellement applicables. Si 0 → bouquet masqué
  // entièrement. Sinon, on affiche les non-applicables barrées.
  const entries = Object.entries(bouquets)
    .map(([key, b]) => {
      const applicableKeys = b.improvements.filter(
        (imp) => applicability[imp]?.applicable !== false,
      );
      const droppedKeys = b.improvements.filter(
        (imp) => applicability[imp]?.applicable === false,
      );
      return { key, b, applicableKeys, droppedKeys };
    })
    .filter(({ applicableKeys }) => applicableKeys.length > 0);

  if (entries.length === 0) {
    // Tous les bouquets recommandés sont vides après filtrage : le
    // logement n'a aucun gain à attendre des bouquets pré-définis.
    return (
      <div className="mt-6 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4">
        <h3 className="text-sm font-bold uppercase tracking-wide text-emerald-900">
          Bouquets recommandés pour votre logement (classe {currentLabel})
        </h3>
        <p className="mt-2 text-xs text-emerald-900">
          <strong>Aucun bouquet pré-défini n'apporterait de gain mesurable
          à votre logement.</strong> Vos équipements (chauffage, ECS,
          ventilation) et votre enveloppe sont déjà au moins aussi
          performants que les recommandations standards. Si vous voulez
          tout de même tester une amélioration spécifique (ex : panneaux
          PV en autoconsommation, remplacement d'un équipement vieillissant),
          composez un scénario sur mesure plus bas.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4">
      <h3 className="text-sm font-bold uppercase tracking-wide text-emerald-900">
        Bouquets recommandés pour votre logement (classe {currentLabel})
      </h3>
      <p className="mt-1 text-xs text-emerald-800">
        {isPasoire ? (
          <>
            Votre logement est classé <strong>{currentLabel}</strong> (passoire
            énergétique). Le guide officiel DPE recommande{" "}
            <strong>2 bouquets de travaux</strong> :
          </>
        ) : (
          <>
            Voici un bouquet d'amélioration ciblé pour passer en classe
            supérieure :
          </>
        )}
      </p>

      <div
        className={`mt-3 grid gap-3 ${
          entries.length === 1 ? "grid-cols-1" : "sm:grid-cols-2"
        }`}
      >
        {entries.map(({ key, b, applicableKeys, droppedKeys }) => {
          const preview = previewFor(key);
          // Le label atteignable RÉEL (après filtrage des gestes non
          // applicables) prime sur la `target_class` figée du YAML.
          const effectiveTarget = preview?.label_after ?? b.target_class;
          const targetIsApproximated =
            preview?.label_after != null &&
            preview.label_after !== b.target_class;
          return (
            <div
              key={key}
              className="rounded border border-emerald-400 bg-white p-3"
            >
              <div className="flex items-start justify-between">
                <h4 className="font-semibold text-emerald-900">{b.label}</h4>
                <span
                  className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800"
                  title={
                    targetIsApproximated
                      ? `Objectif initial du bouquet standard : classe ${b.target_class}. Avec uniquement les gestes applicables à votre logement, l'estimation tombe à ${effectiveTarget}.`
                      : undefined
                  }
                >
                  → classe {effectiveTarget}
                </span>
              </div>
              <p className="mt-2 text-xs text-gray-700">{b.description}</p>
              <ul className="mt-2 list-disc pl-5 text-xs text-gray-700">
                {applicableKeys.map((imp) => (
                  <li key={imp}>{labelFor(imp)}</li>
                ))}
              </ul>
              {droppedKeys.length > 0 && (
                <p className="mt-2 text-[11px] italic text-gray-500">
                  {droppedKeys.length === 1
                    ? "1 amélioration du bouquet standard a été retirée car votre logement en bénéficie déjà."
                    : `${droppedKeys.length} améliorations du bouquet standard ont été retirées car votre logement en bénéficie déjà.`}
                </p>
              )}
              {preview && preview.roi_years != null && (
                <div className="mt-2 rounded bg-gray-50 p-2 text-[11px] text-gray-700">
                  <strong>Estimation :</strong> ROI ~{preview.roi_years} ans
                  {preview.annual_eur_saved > 0 && (
                    <> · économie ~{Math.round(preview.annual_eur_saved).toLocaleString("fr-FR")} €/an</>
                  )}
                  {(preview.relevance === "non_rentable" ||
                    preview.relevance === "inadapte") && (
                    <span className="ml-1 font-semibold text-orange-700">
                      (non rentable sur 20 ans)
                    </span>
                  )}
                </div>
              )}
              {b.notes && b.notes.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-gray-500">
                  {b.notes.map((n, i) => (
                    <li key={i}>💡 {n}</li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => onApply(b.label, applicableKeys)}
                disabled={disabled || applicableKeys.length === 0}
                className="mt-3 w-full rounded bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
              >
                {disabled
                  ? "Création…"
                  : `Simuler ce bouquet (${applicableKeys.length} geste${applicableKeys.length > 1 ? "s" : ""})`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BouquetCard({
  bouquet,
  onClick,
  disabled,
  recommended,
  discouraged,
  applicableCount,
  droppedCount,
  preview,
}: {
  bouquet: BouquetOption;
  onClick: () => void;
  disabled: boolean;
  recommended?: boolean;
  discouraged?: boolean;
  applicableCount?: number;
  droppedCount?: number;
  preview?: BouquetPreview;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative rounded border bg-white p-4 text-left hover:shadow disabled:opacity-50 ${
        discouraged
          ? "border-gray-200 opacity-60 hover:border-gray-300"
          : recommended
            ? "border-emerald-400 hover:border-emerald-600"
            : "border-gray-300 hover:border-blue-700"
      }`}
    >
      {recommended && !discouraged && (
        <span className="absolute -top-2 right-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
          Recommandé
        </span>
      )}
      {discouraged && (
        <span className="absolute -top-2 right-2 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-800">
          ROI &gt; 20 ans
        </span>
      )}
      <h4 className="font-semibold">{bouquet.label ?? bouquet.key}</h4>
      <p className="mt-1 text-xs text-gray-600">{bouquet.description}</p>
      {applicableCount !== undefined ? (
        <>
          <p className="mt-2 text-xs text-gray-700">
            <strong>{applicableCount}</strong> geste
            {applicableCount > 1 ? "s" : ""} applicable
            {applicableCount > 1 ? "s" : ""} à votre logement
          </p>
          {droppedCount !== undefined && droppedCount > 0 && (
            <p className="mt-1 text-[11px] italic text-gray-500">
              {droppedCount} amélioration{droppedCount > 1 ? "s" : ""}{" "}
              du bouquet standard retirée{droppedCount > 1 ? "s" : ""}{" "}
              (déjà en place chez vous)
            </p>
          )}
          {/* Preview du résultat de la simulation : label atteint + ROI.
            * Évite à l'utilisateur de devoir lancer la simulation pour
            * savoir si le bouquet est rentable. */}
          {preview && preview.label_after && (
            <div className="mt-2 rounded bg-gray-50 p-2 text-[11px] text-gray-700">
              {preview.roi_years != null && (
                <div>
                  <strong>ROI ~{preview.roi_years} ans</strong>
                  {preview.annual_eur_saved > 0 && (
                    <> · économie ~{Math.round(preview.annual_eur_saved).toLocaleString("fr-FR")} €/an</>
                  )}
                </div>
              )}
              <div className="mt-0.5">
                Classe {preview.label_before ?? "?"} →{" "}
                <strong>{preview.label_after}</strong>
              </div>
            </div>
          )}
        </>
      ) : (
        bouquet.improvements && (
          <p className="mt-2 text-xs text-gray-500">
            {bouquet.improvements.length} amélioration{bouquet.improvements.length > 1 ? "s" : ""}
          </p>
        )
      )}
    </button>
  );
}

function CustomScenarioBuilder({
  improvements,
  windPotential,
  departmentCode,
  onSubmit,
  loading,
}: {
  improvements: ImprovementOption[];
  windPotential: "fort" | "moyen" | "faible" | null;
  departmentCode: string | null;
  onSubmit: (name: string, keys: string[]) => void;
  loading: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [name, setName] = useState("Mon scénario sur mesure");

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Warning micro-éolien : si l'utilisateur a coché "Installer un micro-
  // éolien" et que son département n'est PAS classé en potentiel "fort"
  // (atlas éolien ADEME), on affiche un avertissement explicite. La
  // rentabilité d'un micro-éolien individuel chute drastiquement en
  // dessous de 5 m/s de vent moyen annuel.
  const microEolienSelected = selected.has("micro_eolien");
  const windWarning =
    microEolienSelected && windPotential && windPotential !== "fort";

  return (
    <details className="mt-6 rounded border border-gray-200 p-4">
      <summary className="cursor-pointer text-sm font-semibold">
        Construire mon propre scénario
      </summary>
      <div className="mt-4 space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-700">
            Nom du scénario
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
          />
        </label>
        <fieldset>
          <legend className="mb-2 text-xs font-medium text-gray-700">
            Améliorations à appliquer
          </legend>
          <div className="space-y-2">
            {improvements.map((imp) => (
              <label key={imp.key} className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.has(imp.key)}
                  onChange={() => toggle(imp.key)}
                />
                <span>
                  <span className="font-medium">{imp.label}</span>
                  {imp.description && (
                    <span className="block text-xs text-gray-600">
                      {imp.description}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        {windWarning && (
          <div className="rounded border border-orange-300 bg-orange-50 p-3 text-xs text-orange-900">
            <strong>⚠️ Micro-éolien peu pertinent pour votre département</strong>
            {departmentCode && (
              <span className="ml-1">({departmentCode})</span>
            )}.
            {" "}
            Le potentiel éolien y est classé <strong>{windPotential}</strong>{" "}
            (Atlas éolien ADEME). Un micro-éolien individuel n'est rentable
            que sur les sites à vent moyen annuel ≥ 5 m/s — typiquement
            côtes Atlantique nord/Manche, Bretagne, Hauts-de-France,
            Languedoc/Provence côtières, Corse. Pour votre département,
            les <strong>panneaux photovoltaïques</strong> (autoconsommation,
            ROI 8-12 ans) sont presque toujours un meilleur investissement
            EnR.
          </div>
        )}
        <button
          type="button"
          disabled={loading || selected.size === 0 || name.trim() === ""}
          onClick={() => onSubmit(name.trim(), Array.from(selected))}
          className="rounded bg-blue-900 px-4 py-2 text-sm text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {loading ? "Calcul…" : `Simuler (${selected.size} amélioration${selected.size > 1 ? "s" : ""})`}
        </button>
      </div>
    </details>
  );
}

/**
 * Convertit en number en gardant null/undefined intacts.
 *
 * Sécurité : si jamais l'API renvoie un Decimal sérialisé en string (cas
 * historique des `cost_estimate_min: Decimal` avant V3), on le rend
 * exploitable sans casser les calculs (NaN sur reste à charge).
 */
function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatEur(n: number | string | null | undefined): string {
  const num = toNum(n);
  if (num === null) return "—";
  return `${Math.round(num).toLocaleString("fr-FR")} €`;
}

function formatKwh(n: number | string | null | undefined): string {
  const num = toNum(n);
  if (num === null) return "—";
  return `${Math.round(num).toLocaleString("fr-FR")} kWh`;
}

/**
 * Module financement (recherche utilisateur : solvabilité = friction n°1).
 * Étale le reste à charge en mensualité (éco-PTZ 0 % sur 15 ans) et, si le
 * revenu mensuel est connu, indique si la mensualité tient dans ~35 % du budget
 * (taux d'endettement usuel). Honnêteté « dette différée » explicite.
 */
const FINANCING_YEARS = 15;
const DEBT_RATIO = 0.35;

function FinancingBlock({
  netCostEur,
  eligibleEcoPtz,
  monthlyIncome,
}: {
  netCostEur: number;
  eligibleEcoPtz: boolean;
  monthlyIncome: number;
}) {
  const months = FINANCING_YEARS * 12;
  const monthly = Math.round(netCostEur / months); // éco-PTZ = 0 % d'intérêt
  const hasIncome = monthlyIncome > 0;
  const maxAffordable = monthlyIncome * DEBT_RATIO;
  const fits = hasIncome && monthly <= maxAffordable;
  const pctOfIncome = hasIncome ? Math.round((monthly / monthlyIncome) * 100) : 0;

  return (
    <div className="mt-3 rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
      <div className="font-semibold">💳 Financement</div>
      <div className="mt-1">
        Reste à charge <strong>{formatEur(netCostEur)}</strong> →{" "}
        {eligibleEcoPtz
          ? <>finançable via <strong>éco-PTZ (0 %)</strong> : ~<strong>{formatEur(monthly)}/mois</strong> sur {FINANCING_YEARS} ans.</>
          : <>~<strong>{formatEur(monthly)}/mois</strong> sur {FINANCING_YEARS} ans via un prêt travaux (hors intérêts).</>}
      </div>
      {hasIncome && (
        <div
          className={`mt-2 rounded px-2 py-1 ${
            fits ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-900"
          }`}
        >
          ≈ {pctOfIncome} % de vos revenus mensuels —{" "}
          {fits
            ? `compatible (sous le seuil d'endettement usuel de ${Math.round(DEBT_RATIO * 100)} %).`
            : `au-dessus du seuil d'endettement usuel de ${Math.round(DEBT_RATIO * 100)} % : à étaler sur une durée plus longue ou à phaser.`}
        </div>
      )}
      <div className="mt-2 text-[11px] text-blue-700">
        ⚠️ Un prêt (même à 0 %) étale le coût mais reste à rembourser : ce n'est
        pas une aide qui réduit le reste à charge.
      </div>
    </div>
  );
}

function ScenarioCard({
  scenario,
  currentLabel,
  improvementsCatalog,
  monthlyIncome,
  onDelete,
  onRecalculate,
  recalculating,
}: {
  scenario: Scenario;
  currentLabel: string;
  improvementsCatalog: ImprovementOption[];
  monthlyIncome: number;
  onDelete: () => void;
  onRecalculate: () => void;
  recalculating: boolean;
}) {
  // Détection obsolescence : scénario calculé avec un moteur antérieur
  // au moteur actuel — résultats potentiellement périmés.
  const isObsolete =
    !!scenario.engine_version &&
    !!scenario.current_engine_version &&
    scenario.engine_version !== scenario.current_engine_version;
  const isInadapte = scenario.relevance === "inadapte";
  const isNonRentable = scenario.relevance === "non_rentable";
  const isMarginal = scenario.relevance === "marginal";
  const isBon = scenario.relevance === "bon";
  const isExcellent = scenario.relevance === "excellent";
  const after = scenario.result_after.energy_label;
  const aids = scenario.estimated_aids;
  // Conversion défensive en number — l'API peut renvoyer string (Decimal
  // historiques sérialisés) ou null.
  const min = toNum(scenario.cost_estimate_min) ?? 0;
  const max = toNum(scenario.cost_estimate_max) ?? 0;
  const totalAids = toNum(aids?.total_eur) ?? 0;
  // Le label "avant" persisté en DB est la référence canonique (recalculée
  // avec le moteur actuel) ; sinon on retombe sur le label courant transmis.
  const labelBefore = scenario.label_before ?? currentLabel;
  const labelFor = (key: string) =>
    improvementsCatalog.find((i) => i.key === key)?.label ?? key;
  const { data: affiliations } = useResolvedAffiliations(
    scenario.improvements,
    "scenario",
  );

  const avgCost = (min + max) / 2;
  const netCost = Math.max(0, avgCost - totalAids);

  const borderColor = isInadapte
    ? "border-red-300"
    : isNonRentable
      ? "border-orange-300"
      : isMarginal
        ? "border-amber-300"
        : isExcellent
          ? "border-emerald-500"
          : isBon
            ? "border-emerald-300"
            : "border-gray-200";

  return (
    <li className={`rounded border bg-white p-4 ${borderColor}`}>
      {/* Bandeaux pertinence — 6 niveaux distincts */}
      {isExcellent && (
        <div className="mb-3 rounded bg-emerald-50 p-2 text-sm text-emerald-900">
          <strong>✅ Très bon investissement.</strong>{" "}
          {scenario.relevance_message ??
            "Ce scénario combine forte économie d'énergie et retour rapide."}
        </div>
      )}
      {isBon && (
        <div className="mb-3 rounded bg-emerald-50 p-2 text-sm text-emerald-900">
          <strong>👍 Bon investissement.</strong>{" "}
          {scenario.relevance_message ??
            "Retour sur investissement raisonnable pour ce logement."}
        </div>
      )}
      {isInadapte && (
        <div className="mb-3 rounded bg-red-50 p-2 text-sm text-red-900">
          <strong>⚠️ Scénario inadapté à ce logement.</strong>{" "}
          {scenario.relevance_message ??
            "Ce scénario ne correspond pas aux besoins de ce logement."}
        </div>
      )}
      {isNonRentable && !isInadapte && (
        <div className="mb-3 rounded bg-orange-50 p-2 text-sm text-orange-900">
          <strong>💰 ROI &gt; 20 ans</strong>
          {" — ce scénario n'est pas économiquement viable sur 20 ans."}
        </div>
      )}
      {isMarginal && !isInadapte && !isNonRentable && (
        <div className="mb-3 rounded bg-amber-50 p-2 text-sm text-amber-900">
          <strong>ℹ️ Gain limité.</strong>{" "}
          {scenario.relevance_message ??
            "Le gain est modéré pour le coût engagé."}
        </div>
      )}

      {/* Améliorations ignorées : soit conflictuelles avec une autre du
       * même type (ex : 2 systèmes de chauffage cochés), soit non
       * pertinentes pour ce logement (équipement existant déjà au moins
       * aussi performant, ex : "Installer une PAC air/eau" sur un logement
       * qui a déjà une PAC géothermique). Le filtrage est automatique. */}
      {scenario.dropped_improvements && scenario.dropped_improvements.length > 0 && (
        <div className="mb-3 rounded border border-yellow-300 bg-yellow-50 p-2 text-xs text-yellow-900">
          <strong>⚠️ Améliorations ignorées (sans effet pour ce logement) :</strong>{" "}
          {scenario.dropped_improvements
            .map((k) => labelFor(k))
            .join(", ")}
          .{" "}
          <span className="text-yellow-800">
            Soit l'équipement déjà en place est au moins aussi performant
            (ex : votre VMC double flux est déjà la cible proposée), soit
            l'amélioration est en conflit avec une autre du même type (ex :
            deux systèmes de chauffage cochés en même temps). Les améliorations
            ignorées ne coûtent rien et n'ouvrent droit à aucune aide dans ce
            scénario.
          </span>
        </div>
      )}

      {/* Bandeau obsolescence — moteur 3CL a évolué depuis le calcul */}
      {isObsolete && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded bg-blue-50 p-2 text-sm text-blue-900">
          <span>
            <strong>Ce scénario a été calculé avec une version
              précédente du moteur 3CL</strong> ({scenario.engine_version} →{" "}
            {scenario.current_engine_version}). Les résultats peuvent ne plus
            correspondre à votre DPE actuel.
          </span>
          <button
            type="button"
            onClick={onRecalculate}
            disabled={recalculating}
            className="rounded bg-blue-700 px-3 py-1 text-xs font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {recalculating ? "Recalcul…" : "Recalculer"}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold">{scenario.name}</h4>
          {scenario.tier && <TierBadge tier={scenario.tier} details={scenario.tier_details ?? null} />}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-500">Avant</span>
            <EnergyLabel label={labelBefore} size="md" />
          </div>
          <span aria-hidden="true" className="text-gray-400">
            →
          </span>
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-500">Après</span>
            <EnergyLabel label={after} size="md" />
          </div>
        </div>
      </div>

      {/* Comparaison consolidée */}
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <KeyValue
          label="Conso (kWh ef / an)"
          value={
            scenario.final_kwh_before !== null && scenario.final_kwh_before !== undefined
              ? `${formatKwh(scenario.final_kwh_before)} → ${formatKwh(scenario.final_kwh_after)}`
              : "—"
          }
        />
        <KeyValue
          label="Économie annuelle"
          value={
            scenario.annual_eur_saved && scenario.annual_eur_saved > 0
              ? `~${formatEur(scenario.annual_eur_saved)} / an`
              : "—"
          }
          highlight="green"
        />
        <KeyValue
          label="Coût (fourchette)"
          value={`${formatEur(min)} – ${formatEur(max)}`}
        />
        <KeyValue
          label="Retour sur investissement"
          value={
            scenario.roi_years && Number(scenario.roi_years) > 0
              ? `~${scenario.roi_years} ans`
              : "—"
          }
          highlight={scenario.roi_years && Number(scenario.roi_years) < 15 ? "green" : undefined}
        />
      </dl>

      {/* Détail financier */}
      <dl className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
        <KeyValue
          label="Aides estimées"
          value={
            aids
              ? `${formatEur(aids.total_eur)} (MPR ${formatEur(aids.maprimerenov_eur)} + CEE ${formatEur(aids.cee_eur)})`
              : "—"
          }
          small
        />
        <KeyValue
          label="Reste à charge (moyen)"
          value={formatEur(netCost)}
          small
        />
        {aids?.eligible_eco_ptz && (
          <KeyValue
            label="Éco-PTZ"
            value="Éligible (prêt à taux zéro)"
            small
          />
        )}
      </dl>

      {netCost > 0 && (
        <FinancingBlock
          netCostEur={netCost}
          eligibleEcoPtz={!!aids?.eligible_eco_ptz}
          monthlyIncome={monthlyIncome}
        />
      )}

      {/* Détail par amélioration (F-TRAV-003 enrichi V2) */}
      {scenario.breakdown && scenario.breakdown.length > 0 ? (
        <>
          <ScenarioBreakdownTable
            breakdown={scenario.breakdown}
            droppedKeys={scenario.dropped_improvements ?? []}
            droppedReasons={scenario.dropped_improvements_reasons ?? {}}
            improvementsCatalog={improvementsCatalog}
          />
          <SummerComfortRanking breakdown={scenario.breakdown} />
        </>
      ) : (
        <ul className="mt-3 list-disc pl-5 text-xs text-gray-600">
          {scenario.improvements.map((key) => (
            <li key={key}>{labelFor(key)}</li>
          ))}
        </ul>
      )}

      {/* Bloc Rénovation d'ampleur + Accompagnateur Rénov' (F-TRAV-005) */}
      {scenario.ampleur?.is_eligible && (
        <AmpleurBlock ampleur={scenario.ampleur} tier={scenario.tier ?? "violet"} />
      )}

      {/* Calendrier d'amortissement visuel (V4 UX-12) */}
      {!isInadapte && scenario.annual_eur_saved && Number(scenario.annual_eur_saved) > 0 && (
        <div className="mt-4">
          <AmortizationChart
            netCostEur={netCost}
            annualSavingsEur={Number(scenario.annual_eur_saved)}
            roiYears={
              scenario.roi_years && Number(scenario.roi_years) > 0
                ? Number(scenario.roi_years)
                : null
            }
          />
        </div>
      )}

      {/* Alertes contre-performances détectées (V4 — guide DPE Annexe 2) */}
      {scenario.risk_alerts && scenario.risk_alerts.length > 0 && (
        <div className="mt-4 space-y-2">
          {scenario.risk_alerts.map((alert) => (
            <div
              key={alert.code}
              className={`rounded border p-2 text-xs ${
                alert.severity === "warning"
                  ? "border-orange-300 bg-orange-50 text-orange-900"
                  : "border-blue-200 bg-blue-50 text-blue-900"
              }`}
            >
              <strong>
                {alert.severity === "warning" ? "⚠️ " : "ℹ️ "}{alert.title}
              </strong>
              <div className="mt-1">{alert.description}</div>
            </div>
          ))}
        </div>
      )}

      {aids?.notes && aids.notes.length > 0 && (
        <ul className="mt-3 list-disc pl-5 text-xs text-gray-500">
          {aids.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}

      <AffiliationLinks items={affiliations} title="Passer à l'action" />

      <div className="mt-4 flex justify-end gap-4">
        {!isObsolete && (
          <button
            type="button"
            onClick={onRecalculate}
            disabled={recalculating}
            className="text-xs text-blue-700 underline disabled:opacity-50"
          >
            {recalculating ? "Recalcul…" : "Recalculer"}
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          className="text-xs text-red-700 underline"
        >
          Supprimer ce scénario
        </button>
      </div>
    </li>
  );
}

function ScenarioBreakdownTable({
  breakdown,
  droppedKeys,
  droppedReasons,
  improvementsCatalog,
}: {
  breakdown: ImprovementBreakdown[];
  droppedKeys: string[];
  droppedReasons: Record<string, string>;
  improvementsCatalog: ImprovementOption[];
}) {
  const labelFor = (key: string) =>
    improvementsCatalog.find((i) => i.key === key)?.label ?? key;

  // Traduit le code raison renvoyé par le backend en phrase lisible.
  // Si la raison est absente (scénario créé avant l'ajout du champ
  // dropped_improvements_reasons), on retourne `short = null` pour que
  // le badge n'affiche pas "IGNORÉE — Ignorée" mais juste "IGNORÉE".
  const explainReason = (
    key: string,
  ): { short: string | null; long: string; color: "orange" | "blue" | "gray" } => {
    const raw = droppedReasons[key] ?? "";
    if (raw.startsWith("conflict_with:")) {
      const otherKey = raw.slice("conflict_with:".length);
      const otherLabel = labelFor(otherKey);
      return {
        short: "Conflit",
        long: `En conflit avec "${otherLabel}" (déjà cochée dans le scénario, même type — une seule peut s'appliquer à la fois).`,
        color: "orange",
      };
    }
    if (raw === "already_optimal") {
      return {
        short: "Déjà optimal",
        long: "Votre équipement existant est déjà au moins aussi performant — aucun gain énergétique mesurable n'est attendu de cette amélioration sur ce logement.",
        color: "blue",
      };
    }
    if (raw === "roi_too_long") {
      return {
        short: "ROI > 20 ans",
        long: "Cette amélioration apporte un gain énergétique mais son coût ne se rentabilise pas en 20 ans sur ce logement (gain économique annuel trop faible par rapport au coût net après aides). Elle reste envisageable pour le confort ou la valeur de revente, mais n'est pas un investissement énergétique pertinent isolément.",
        color: "orange",
      };
    }
    // Pas de raison fournie (scénario créé avant l'ajout du champ).
    // Message générique sans répétition du mot "Ignorée".
    return {
      short: null,
      long:
        "Raison du filtrage non enregistrée pour ce scénario (créé avec une " +
        "version antérieure du moteur). Cliquez sur Recalculer ci-dessous " +
        "pour rafraîchir et voir la raison précise (conflit ou déjà optimal).",
      color: "gray",
    };
  };
  return (
    <div className="mt-4 overflow-x-auto">
      <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
        Bénéfice par amélioration
      </h5>
      <table className="w-full text-xs">
        <thead className="text-left text-gray-500">
          <tr>
            <th className="py-1 pr-2 font-medium">Amélioration</th>
            <th className="py-1 pr-2 font-medium">Étiquette</th>
            <th className="py-1 pr-2 font-medium text-right">kWh évités</th>
            <th className="py-1 pr-2 font-medium text-right">€ / an</th>
            <th className="py-1 pr-2 font-medium text-right">Coût</th>
            <th className="py-1 font-medium text-right">Aides</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.map((b) => (
            <tr key={b.key} className="border-t border-gray-100">
              <td className="py-1.5 pr-2">{b.label}</td>
              <td className="py-1.5 pr-2 whitespace-nowrap">
                {b.label_before === b.label_after ? (
                  <span className="text-gray-400">{b.label_before} =</span>
                ) : (
                  <span>
                    <span className="text-gray-500">{b.label_before}</span>
                    <span className="mx-1 text-gray-400">→</span>
                    <span className="font-semibold text-emerald-700">
                      {b.label_after}
                    </span>
                  </span>
                )}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {b.final_kwh_saved > 0 ? (
                  <span className="text-emerald-700">
                    -{formatKwh(b.final_kwh_saved)}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {b.annual_eur_saved > 0 ? (
                  <span className="text-emerald-700">
                    {formatEur(b.annual_eur_saved)}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                {formatEur(b.cost_min_eur)} – {formatEur(b.cost_max_eur)}
              </td>
              <td className="py-1.5 text-right tabular-nums text-blue-700">
                {b.aids_eur > 0 ? formatEur(b.aids_eur) : "—"}
              </td>
            </tr>
          ))}
          {/* Améliorations ignorées (conflit ou déjà optimal) : on les
            * liste en bas du tableau en gris pour que l'utilisateur voie
            * d'un coup d'œil toutes ses cases cochées, y compris celles
            * qui n'ont pas été appliquées. La raison spécifique est
            * affichée par badge + ligne explicative en dessous. */}
          {droppedKeys.map((k) => {
            const reason = explainReason(k);
            const badgeClass =
              reason.color === "orange"
                ? "bg-orange-100 text-orange-800"
                : reason.color === "blue"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-200 text-gray-700";
            return (
              <tr
                key={`dropped-${k}`}
                className="border-t border-gray-100 bg-gray-50/60"
              >
                <td className="py-1.5 pr-2 text-gray-500" colSpan={6}>
                  <div className="flex items-start gap-2">
                    <span className="line-through">{labelFor(k)}</span>
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold no-underline ${badgeClass}`}
                    >
                      {reason.short ? `IGNORÉE — ${reason.short}` : "IGNORÉE"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] italic text-gray-500 no-underline">
                    {reason.long}
                  </p>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {droppedKeys.length > 0 && (
        <p className="mt-1 text-[10px] italic text-gray-500">
          Lignes barrées = améliorations cochées mais non appliquées (déjà
          en place sur votre logement, ou en conflit avec une autre du même
          type). Aucun coût, aucune aide. Voir le bandeau jaune en haut de
          la carte pour le détail.
        </p>
      )}
    </div>
  );
}

/**
 * Classement "confort d'été" — pour chaque amélioration du scénario, on
 * affiche le gain (ou perte) de points de confort d'été. Les améliorations
 * sont triées par delta décroissant pour mettre en avant les plus impactantes.
 * Source : Guide DPE v3 p. 29-30 (indicateur officiel 0-100).
 */
function SummerComfortRanking({
  breakdown,
}: {
  breakdown: ImprovementBreakdown[];
}) {
  // Filtre les améliorations qui ont une donnée confort d'été (champ optionnel).
  const items = breakdown
    .filter((b) => b.summer_score_before != null && b.summer_score_after != null)
    .map((b) => ({
      key: b.key,
      label: b.label,
      before: b.summer_score_before ?? 0,
      after: b.summer_score_after ?? 0,
      classeBefore: b.summer_classe_before ?? "",
      classeAfter: b.summer_classe_after ?? "",
      delta: b.summer_score_delta ?? 0,
    }))
    .sort((a, b) => b.delta - a.delta);

  if (items.length === 0) return null;

  // Score initial du scénario (avant toute amélioration) : c'est le "before" du
  // premier item après tri par ordre du breakdown… mais le ranking est par
  // delta. On utilise le min de tous les "before" comme score initial pour le
  // sommaire global (en pratique = score AVANT travaux du logement).
  const initialScore = breakdown[0]?.summer_score_before ?? 0;
  const finalScore = breakdown[breakdown.length - 1]?.summer_score_after ?? 0;
  const totalDelta = finalScore - initialScore;

  const classeBadge = (c: string) => {
    if (c === "bon") return "bg-emerald-100 text-emerald-800";
    if (c === "moyen") return "bg-amber-100 text-amber-800";
    if (c === "insuffisant") return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-700";
  };

  return (
    <div className="mt-5 rounded border border-amber-200 bg-amber-50/50 p-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h5 className="text-xs font-semibold uppercase tracking-wide text-amber-900">
          ☀️ Confort d'été — gain par amélioration
        </h5>
        <span className="text-xs text-amber-900">
          Score global : <strong>{initialScore}</strong> →{" "}
          <strong>{finalScore}</strong> /100
          {totalDelta !== 0 && (
            <span
              className={`ml-1 font-semibold ${totalDelta > 0 ? "text-emerald-700" : "text-red-700"}`}
            >
              ({totalDelta > 0 ? "+" : ""}
              {totalDelta} pts)
            </span>
          )}
        </span>
      </div>
      <p className="mb-2 text-[11px] text-amber-900">
        Indicateur officiel Guide DPE v3 (toiture, protections solaires
        extérieures, inertie, traversance). Plus le score est haut, plus le
        logement reste frais en été sans climatisation.
      </p>
      <table className="w-full text-xs">
        <thead className="text-left text-gray-600">
          <tr>
            <th className="py-1 pr-2 font-medium">Amélioration</th>
            <th className="py-1 pr-2 font-medium">Classe (avant → après)</th>
            <th className="py-1 pr-2 font-medium text-right">Score</th>
            <th className="py-1 font-medium text-right">Gain</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.key} className="border-t border-amber-200/60">
              <td className="py-1.5 pr-2">{it.label}</td>
              <td className="py-1.5 pr-2 whitespace-nowrap">
                {it.classeBefore === it.classeAfter ? (
                  <span
                    className={`rounded px-1.5 py-0.5 capitalize ${classeBadge(it.classeBefore)}`}
                  >
                    {it.classeBefore || "—"}
                  </span>
                ) : (
                  <>
                    <span
                      className={`rounded px-1.5 py-0.5 capitalize ${classeBadge(it.classeBefore)}`}
                    >
                      {it.classeBefore || "—"}
                    </span>
                    <span className="mx-1 text-gray-400">→</span>
                    <span
                      className={`rounded px-1.5 py-0.5 capitalize ${classeBadge(it.classeAfter)}`}
                    >
                      {it.classeAfter || "—"}
                    </span>
                  </>
                )}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                {it.before} → {it.after}
              </td>
              <td
                className={`py-1.5 text-right tabular-nums font-semibold ${
                  it.delta > 0
                    ? "text-emerald-700"
                    : it.delta < 0
                      ? "text-red-700"
                      : "text-gray-400"
                }`}
              >
                {it.delta > 0 ? "+" : ""}
                {it.delta || "—"}
                {it.delta !== 0 && " pts"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const TIER_COLORS: Record<string, string> = {
  bleu: "bg-blue-100 text-blue-900 border-blue-300",
  jaune: "bg-yellow-100 text-yellow-900 border-yellow-300",
  violet: "bg-purple-100 text-purple-900 border-purple-300",
  rose: "bg-pink-100 text-pink-900 border-pink-300",
};

const TIER_LABEL: Record<string, string> = {
  bleu: "Bleu (très modestes)",
  jaune: "Jaune (modestes)",
  violet: "Violet (intermédiaires)",
  rose: "Rose (supérieurs)",
};

function TierBadge({
  tier,
  details,
}: {
  tier: "bleu" | "jaune" | "violet" | "rose";
  details: Scenario["tier_details"];
}) {
  const isDefault = details?.source === "default";
  return (
    <div className="mt-1 inline-flex items-center gap-2">
      <span
        className={`rounded-full border px-2 py-0.5 text-xs font-medium ${TIER_COLORS[tier] ?? ""}`}
        title={
          isDefault
            ? "Tier par défaut (données fiscales non renseignées)"
            : `RFR ${details?.tax_reference_income_eur?.toLocaleString("fr-FR")} € / ${details?.household_size} pers. ${
                details?.is_idf ? "(Île-de-France)" : "(hors IDF)"
              }`
        }
      >
        MaPrimeRénov' {TIER_LABEL[tier] ?? tier}
      </span>
      {isDefault && (
        <span className="text-xs text-gray-500">
          (par défaut, complétez votre profil)
        </span>
      )}
    </div>
  );
}

function AmpleurBlock({
  ampleur,
  tier,
}: {
  ampleur: NonNullable<Scenario["ampleur"]>;
  tier: string;
}) {
  return (
    <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm">
      <div className="flex items-start gap-2">
        <span className="text-lg" aria-hidden>🏗️</span>
        <div>
          <h5 className="font-semibold text-emerald-900">
            Éligible au parcours « Rénovation d'ampleur » MaPrimeRénov'
          </h5>
          <p className="mt-1 text-xs text-emerald-900">
            Gain de <strong>{ampleur.class_jumps} classes DPE</strong> +{" "}
            {ampleur.envelope_gestures} gestes d'enveloppe → taux MPR{" "}
            <strong>{Math.round(ampleur.rate_pct * 100)} %</strong> sur un
            plafond de {formatEur(ampleur.ceiling_eur)} (tier {tier}).
          </p>
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
        <KeyValue
          label="MPR rénovation d'ampleur"
          value={formatEur(ampleur.mpr_eur)}
          highlight="green"
          small
        />
        <KeyValue
          label="Accompagnateur Rénov' (forfait)"
          value={formatEur(ampleur.accompagnateur_cost_eur)}
          small
        />
        <KeyValue
          label="Aide Accompagnateur (MPR)"
          value={`${formatEur(ampleur.accompagnateur_aid_eur)} (${
            ampleur.accompagnateur_cost_eur > 0
              ? Math.round(
                  (ampleur.accompagnateur_aid_eur /
                    ampleur.accompagnateur_cost_eur) *
                    100,
                )
              : 0
          }%)`}
          highlight="green"
          small
        />
      </dl>

      <p className="mt-2 text-xs text-emerald-800">
        <strong>Mon Accompagnateur Rénov'</strong> est{" "}
        <em>obligatoire</em> pour ce parcours : prise en charge à{" "}
        {Math.round(
          (ampleur.accompagnateur_aid_eur /
            Math.max(1, ampleur.accompagnateur_cost_eur)) *
            100,
        )}{" "}
        % par MPR (plafond 2 000 €).{" "}
        <a
          className="underline"
          href="https://france-renov.gouv.fr/preparer-projet/mon-accompagnateur-renov"
          target="_blank"
          rel="noreferrer"
        >
          En savoir plus
        </a>
        .
      </p>
    </div>
  );
}

function KeyValue({
  label,
  value,
  highlight,
  small,
}: {
  label: string;
  value: string;
  highlight?: "green";
  small?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd
        className={`mt-1 font-medium ${small ? "text-xs" : ""} ${
          highlight === "green" ? "text-emerald-700" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
